import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  LinearProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import type { ImportStats } from './importUtils';

interface ImportConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  stats: ImportStats;
  variant: 'color' | 'size';
  loading?: boolean;
  progress?: number;
}

export const ImportConfirmationDialog: React.FC<ImportConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  stats,
  variant,
  loading = false,
  progress = 0,
}) => {
  const [showAllErrors, setShowAllErrors] = useState(false);

  const hasErrors = stats.totalErrors > 0;
  const hasChanges = stats.toAdd > 0 || stats.toUpdate > 0;
  const displayedErrors = showAllErrors ? stats.errors : stats.errors.slice(0, 5);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Confirm {variant === 'color' ? 'Color' : 'Size'} Properties Import
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Importing {variant} properties... {progress}%
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {!loading && (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              The import file contains the following changes:
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  To be added:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats.toAdd}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  To be updated:
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {stats.toUpdate}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Unchanged:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stats.unchanged}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="error">
                  Errors:
                </Typography>
                <Typography variant="body2" color="error" fontWeight="bold">
                  {stats.totalErrors}
                </Typography>
              </Box>
            </Box>

            {hasErrors && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                  Validation Errors:
                </Typography>
                <List dense>
                  {displayedErrors.map((error, index) => (
                    <ListItem key={index} disableGutters>
                      <ListItemText
                        primary={error}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
                {stats.errors.length > 5 && (
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => setShowAllErrors(!showAllErrors)}
                    >
                      <ExpandMoreIcon
                        sx={{
                          transform: showAllErrors ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                      />
                    </IconButton>
                    <Typography variant="caption" display="block">
                      {showAllErrors ? 'Show less' : `Show ${stats.errors.length - 5} more errors`}
                    </Typography>
                  </Box>
                )}
              </Alert>
            )}

            {!hasErrors && !hasChanges && (
              <Alert severity="info">
                No changes detected. All records in the import file match existing data.
              </Alert>
            )}

            {!hasErrors && hasChanges && (
              <Alert severity="success">
                Ready to import. Click Confirm to proceed.
              </Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={hasErrors || !hasChanges || loading}
        >
          {loading ? 'Importing...' : 'Confirm'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
